import React, { useState, useEffect } from 'react';
import { useAppState } from '../src/contexts/AppStateContext';
import { useAuth } from '../src/contexts/AuthContext';
import OrgSwitcher from './OrgSwitcher';
import EventSwitcher from './EventSwitcher';
import DisplayEvent from '../src/events/DisplayEvent';
import CreateEvent from './events/CreateEvent';
import TicketManagement from './tickets/TicketManagement';
import ZoneManagement from './zones//ZoneManagement';
import EventAccessManagement from './EventAccessManagement';
import BadgeScanner from './BadgeScanner';
import TicketDesign from './TicketDesign';
import SubmissionList from './submissions/SubmissionList';
import FormBuilder from './form-builder/FormBuilder';
import TeamManagement from './TeamManagement';
import CheckInList from './checkins/CheckInList';
import {
  Calendar,
  Ticket,
  Users,
  Map,
  Shield,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  QrCode,
  PaintBucket
} from 'lucide-react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router';

const Dashboard = () => {
  const { currentOrganization, currentEvent, events, switchEvent, hasRole } = useAppState();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);

  // Active tab comes from the URL path (/tickets, /submissions, ...)
  const { tab } = useParams();
  const activeTab = tab || 'event-details';

  // Redirect legacy ?tab=... links to the path-based URL
  useEffect(() => {
    const legacyTab = searchParams.get('tab');
    if (legacyTab) {
      const params = new URLSearchParams(searchParams);
      params.delete('tab');
      navigate({ pathname: `/${legacyTab}`, search: params.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  // Handle window resize to auto-open sidebar on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigate to the tab's path, keeping query params (org, event, ...) intact
  const updateActiveTab = (tabId) => {
    navigate({ pathname: `/${tabId}`, search: location.search });
  };

  // Define organization-level navigation items
  const orgNavItems = [
    {
      id: 'team',
      name: 'Team Management',
      icon: <Users size={20} />,
      roles: ['owner', 'admin']
    }
  ];

  // Define event-level navigation items
  const eventNavItems = [
    {
      id: 'event-details',
      name: 'Event Details',
      icon: <Calendar size={20} />,
      roles: ['owner', 'admin', 'event_manager', 'usher', 'submissions_manager']
    },
    {
      id: 'submissions',
      name: 'Submissions',
      icon: <FileText size={20} />,
      roles: ['owner', 'admin', 'event_manager', 'usher', 'submissions_manager']
    },
    {
      id: 'checkins',
      name: 'Checkins',
      icon: <FileText size={20} />,
      roles: ['owner', 'admin', 'event_manager', 'usher']
    },
    {
      id: 'tickets',
      name: 'Tickets',
      icon: <Ticket size={20} />,
      roles: ['owner', 'admin', 'event_manager']
    },
    {
      id: 'ticket-design',
      name: 'Ticket Design',
      icon: <PaintBucket size={20} />,
      roles: ['owner', 'admin', 'event_manager']
    },
    {
      id: 'zones',
      name: 'Zones',
      icon: <Map size={20} />,
      roles: ['owner', 'admin', 'event_manager']
    },
    {
      id: 'form-builder',
      name: 'Form Builder',
      icon: <FileText size={20} />,
      roles: ['owner', 'admin', 'event_manager']
    },
    {
      id: 'scanner',
      name: 'Badge Scanner',
      icon: <QrCode size={20} />,
      roles: ['owner', 'admin', 'event_manager', 'usher']
    },
    {
      id: 'access',
      name: 'Access Management',
      icon: <Shield size={20} />,
      roles: ['owner', 'admin']
    }
  ];

  // Get filtered navigation items based on user's role
  const filteredOrgNavItems = orgNavItems.filter(item => hasRole(item.roles));
  const filteredEventNavItems = eventNavItems.filter(item => hasRole(item.roles));

  // Function to render the active tab content
  const renderTabContent = () => {
    if (activeTab === 'create-event' && hasRole(['owner', 'admin'])) {
      return (
        <CreateEvent />
      );
    }

    // Handle organization-level tabs
    if (activeTab === 'team') {
      if (!hasRole(['owner', 'admin'])) {
        return (
          <div className="p-6 text-center">
            <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
            <p className="text-gray-500">You don't have permission to view this section.</p>
          </div>
        );
      }
      return <TeamManagement />;
    }

    // For event-level tabs, check if event is selected
    if (!currentEvent) return <div className="p-6 text-center">Please select an event</div>;

    // Check if user has access to the active tab
    const currentTab = eventNavItems.find(item => item.id === activeTab);
    if (currentTab && !hasRole(currentTab.roles)) {
      return (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-4">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to view this section.</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'event-details':
        return <DisplayEvent />;
      case 'submissions':
        return <SubmissionList />;
      case 'tickets':
        return <TicketManagement />;
      case 'zones':
        return <ZoneManagement />;
      case 'access':
        return <EventAccessManagement />;
      case 'scanner':
        return <BadgeScanner />;
      case 'ticket-design':
        return <TicketDesign />;
      case 'create-event':
        return <CreateEvent />;
      case 'form-builder':
        return <FormBuilder />;
      case 'checkins':
        return <CheckInList />;
      default:
        return (
          <div className="p-6 text-center">
            <h2 className="text-xl font-semibold mb-4">
              {[...orgNavItems, ...eventNavItems].find(item => item.id === activeTab)?.name}
            </h2>
            <p className="text-gray-500">This section is under development.</p>
          </div>
        );
    }
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Check if user can create events (only owner and admin)
  const canCreateEvent = hasRole(['owner', 'admin']);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      {/* Sidebar open button - only shown when the sidebar is closed */}
      {!sidebarOpen && (
        <div className="fixed top-4 left-4 z-50">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md bg-white shadow-md text-gray-500 hover:text-gray-600 focus:outline-none"
          >
            <Menu size={24} />
          </button>
        </div>
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg flex flex-col transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } transition-transform duration-300 ease-in-out`}
      >
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-800">Event Manager</h1>
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100 focus:outline-none"
            >
              <X size={24} />
            </button>
          </div>
          <div className="mt-4">
            <OrgSwitcher />
          </div>

          {/* Organization-level navigation - directly under OrgSwitcher */}
          {currentOrganization && filteredOrgNavItems.length > 0 && (
            <div className="mt-4">
              <ul className="space-y-1">
                {filteredOrgNavItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => updateActiveTab(item.id)}
                      className={`flex items-center w-full px-3 py-2 text-sm rounded-md ${activeTab === item.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-700 hover:bg-gray-100'
                        } transition-colors`}
                    >
                      <span className="mr-3">{item.icon}</span>
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          <nav className="mt-4 px-2">

            {/* Event-level navigation */}
            {currentEvent ? (
              <div className="mb-6">
                <div className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {currentEvent.name}
                </div>
                <ul>
                  {filteredEventNavItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => updateActiveTab(item.id)}
                        className={`flex items-center w-full px-3 py-2 my-1 text-sm rounded-md ${activeTab === item.id
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-700 hover:bg-gray-100'
                          } transition-colors`}
                      >
                        <span className="mr-3">{item.icon}</span>
                        {item.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500">
                Please select an event to manage
              </div>
            )}
          </nav>
        </div>

        {/* Logout button */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <button
            onClick={logout}
            className="flex items-center w-full px-3 py-2 text-sm text-red-600 rounded-md hover:bg-red-50 transition-colors"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content - PROPERLY POSITIONED */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out ${sidebarOpen ? 'ml-64' : 'ml-0'
        }`}>
        {/* Top navigation */}
        <header className="bg-white shadow flex-shrink-0">
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-800 truncate">
                {[...orgNavItems, ...eventNavItems].find(item => item.id === activeTab)?.name}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              {canCreateEvent && (
                <button
                  onClick={() => updateActiveTab('create-event')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors text-sm"
                >
                  Create Event
                </button>
              )}
              {/* Only show EventSwitcher for event-level tabs */}
              {!orgNavItems.some(item => item.id === activeTab) && (
                <div className="w-64">
                  <EventSwitcher />
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content area */}
        <main className="flex-1 overflow-y-auto p-4">
          <div className="bg-white rounded-lg shadow p-4 min-h-full">
            {renderTabContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;